import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Control, useForm } from "react-hook-form";

import { SkillSummary, SkillVersionRecord } from "@/shared/domain/skills";
import {
  useCreateVersion,
  useSkillVersions,
  useUpdateVersion,
} from "@/shared/domain/skills/hooks";

import { useRelevantTopics } from "./relevantTopics";
import {
  skillVersionFormSchema,
  SkillVersionFormValues,
} from "./skillVersionFormSchema";

export type { SkillVersionFormValues };

const EMPTY_VALUES: SkillVersionFormValues = { description: "", body: "" };

export interface SkillVersionForm {
  /** Bind the fields with `<Controller>`; see `SkillVersionFields`. */
  control: Control<SkillVersionFormValues>;
  /** Org topic catalog, for `[[topic]]` autocomplete in the body editor. */
  topicNames: string[];
  /** The version the form is bound to; null until the versions list resolves. */
  target: SkillVersionRecord | null;
  /** Last save failure, cleared when a new save starts. */
  errorMessage: string | null;
  /** True while a save is in flight — drive a button spinner off this. */
  isMutating: boolean;
  /** True while saving must be held: mid-save, or missing a prerequisite. */
  isSaveBlocked: boolean;
  /**
   * Overwrite the bound version. Resolves true when the save succeeded, and
   * false when it failed *or* when validation rejected the values — in the
   * latter case the field errors are already on screen.
   */
  save: () => Promise<boolean>;
  /**
   * Publish the edits as a new version. Resolves to the created record so
   * callers can move their selection onto it, or null when the save failed or
   * the values didn't validate.
   */
  saveAsNewVersion: () => Promise<SkillVersionRecord | null>;
}

function latestVersion(
  versions: SkillVersionRecord[] | undefined,
): SkillVersionRecord | null {
  if (!versions || versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

/** The requested version, or the live one when no version was requested. */
export function pickVersion(
  versions: SkillVersionRecord[] | undefined,
  requested: number | null | undefined,
): SkillVersionRecord | null {
  if (requested !== null && requested !== undefined) {
    return versions?.find((v) => v.version === requested) ?? null;
  }
  return latestVersion(versions);
}

export interface UseSkillVersionFormOptions {
  /**
   * True while the form is on screen. Flipping it false → true reseeds the
   * fields from the stored version, so leaving and re-entering an edit surface
   * discards whatever was typed and abandoned.
   */
  active: boolean;
  summary: SkillSummary | null;
  /** Version to edit. Null edits the live version. */
  version: number | null;
  orgId: string | undefined;
}

/**
 * Editing state for one skill version — the form values, the two save paths
 * (overwrite in place / publish as a new version), and the gating that keeps a
 * save from firing before its prerequisites are loaded.
 *
 * Field state and validation are react-hook-form's, against
 * {@link skillVersionFormSchema}. What this hook adds on top is the part a
 * resolver can't express: which version a save writes to, when Save is held
 * because a prerequisite hasn't loaded, and turning a rejected mutation into a
 * message rather than an unhandled rejection.
 */
export function useSkillVersionForm({
  active,
  summary,
  version,
  orgId,
}: UseSkillVersionFormOptions): SkillVersionForm {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { control, handleSubmit, reset } = useForm<SkillVersionFormValues>({
    resolver: zodResolver(skillVersionFormSchema),
    defaultValues: EMPTY_VALUES,
  });

  const versionsQuery = useSkillVersions(summary?.skill.skill_id);
  const createVersion = useCreateVersion(orgId);
  const updateVersion = useUpdateVersion(orgId);
  // Scoped to `active`: the inspector keeps this hook mounted while it's just
  // displaying a version, and an idle dialog shouldn't pull the org's whole
  // topic catalog.
  const { topicNames, isCatalogLoading, relevantTopicsFor } = useRelevantTopics(
    orgId,
    { enabled: active },
  );

  const target = pickVersion(versionsQuery.data, version);

  // Reseed on activation and whenever the *identity* of the bound version
  // changes — keyed on the version number rather than on `versionsQuery.data`,
  // so a refetch (tab focus, post-save invalidation) never clobbers typing in
  // progress. Including the resolved number also covers the case where the
  // form opens before the versions list lands: the first seed falls back to
  // `latest_version`, and the real target replaces it the moment it resolves
  // rather than leaving the wrong body in the fields.
  const targetVersion = target?.version ?? null;
  React.useEffect(() => {
    if (!active) {
      return;
    }
    const seed = target ?? summary?.latest_version ?? null;
    // `reset`, not per-field `setValue`: it reseeds the values *and* clears the
    // dirty / error state, so re-entering the editor doesn't arrive showing
    // complaints about the values the last abandoned edit left behind.
    reset(
      seed === null
        ? EMPTY_VALUES
        : { description: seed.description, body: seed.body },
    );
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
  }, [active, summary?.skill.skill_id, targetVersion, reset]);

  const isMutating = createVersion.isPending || updateVersion.isPending;
  // Saving needs the resolved target version (otherwise Save would silently
  // write nothing) and a settled topic catalog (see `useRelevantTopics`).
  const isSaveBlocked = isMutating || target === null || isCatalogLoading;

  /** Run a save, routing a rejection into `errorMessage` instead of throwing. */
  async function runSave<T>(
    mutate: () => Promise<T>,
    fallbackMessage: string,
  ): Promise<T | null> {
    setErrorMessage(null);
    try {
      return await mutate();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : fallbackMessage);
      return null;
    }
  }

  /**
   * Validate, then save — reporting back whether anything was written.
   *
   * `handleSubmit` skips its callback entirely when the resolver rejects, and
   * discards whatever the callback returns, so the outcome is captured in a
   * local instead. That leaves both callers with one answer to branch on: a
   * null result means nothing was written, whether the values were invalid or
   * the request failed.
   */
  async function submit<T>(
    write: (values: SkillVersionFormValues) => Promise<T | null>,
  ): Promise<T | null> {
    let result: T | null = null;
    await handleSubmit(async (values) => {
      result = await write(values);
    })();
    return result;
  }

  const save = React.useCallback(async (): Promise<boolean> => {
    if (summary === null || target === null) {
      return false;
    }
    const saved = await submit((values) =>
      runSave(
        () =>
          updateVersion.mutateAsync({
            skillId: summary.skill.skill_id,
            version: target.version,
            request: {
              description: values.description,
              body: values.body,
              relevant_topics: relevantTopicsFor(values.body),
            },
          }),
        "Failed to save.",
      ),
    );
    return saved !== null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runSave/submit are stable local closures over setState and RHF's handleSubmit
  }, [handleSubmit, relevantTopicsFor, summary, target, updateVersion]);

  const saveAsNewVersion =
    React.useCallback(async (): Promise<SkillVersionRecord | null> => {
      if (summary === null || target === null) {
        return null;
      }
      return submit((values) =>
        runSave(
          () =>
            createVersion.mutateAsync({
              skillId: summary.skill.skill_id,
              request: {
                description: values.description,
                body: values.body,
                relevant_topics: relevantTopicsFor(values.body),
              },
            }),
          "Failed to create version.",
        ),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps -- runSave/submit are stable local closures over setState and RHF's handleSubmit
    }, [createVersion, handleSubmit, relevantTopicsFor, summary, target]);

  return {
    control,
    topicNames,
    target,
    errorMessage,
    isMutating,
    isSaveBlocked,
    save,
    saveAsNewVersion,
  };
}
